import { Component, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannelTypeEnum,
  ChatCommandTypeEnum,
  ChatSocketService,
} from 'lib-trend-core';
import { takeUntil } from 'rxjs/operators';
import * as ChatActions from '../../state/chat.actions';

/**
 * Internal Chat "Novo canal" dialog (SPEC-2026-07-internal-chat, T-18).
 *
 * Sends CHANNEL_CREATE `{ type: PUBLIC|PRIVATE, name, description }` over the
 * websocket command path. The ack carries no channelId — the created channel
 * opens via the `chat:channel:updated {action:'created'}` fan-out handled by
 * ChatSocketBridgeService, and the sidebar refreshes via the bootstrap effect.
 */
@Component({
  templateUrl: './chat-channel-create-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatChannelCreateDialogComponent extends AbstractComponent {

  readonly typePublic = ChatChannelTypeEnum.PUBLIC;
  readonly typePrivate = ChatChannelTypeEnum.PRIVATE;

  submitting = false;

  channelForm = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    description: new FormControl<string>(''),
    type: new FormControl<ChatChannelTypeEnum>(ChatChannelTypeEnum.PUBLIC, [Validators.required]),
  });

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatChannelCreateDialogComponent>,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
  ) {
    super(injector);
  }

  /** The visibility toggle reads/writes the same `type` control the command sends. */
  get isPrivate(): boolean {
    return this.channelForm.value.type === ChatChannelTypeEnum.PRIVATE;
  }

  onPrivateToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement)?.checked ?? false;
    this.channelForm.controls.type.setValue(
      checked ? ChatChannelTypeEnum.PRIVATE : ChatChannelTypeEnum.PUBLIC,
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  create(): void {
    if (this.submitting) return;

    const name = (this.channelForm.value.name ?? '').trim();
    if (!name) {
      // Surfaces the inline feedback under the field, not just the toast.
      this.channelForm.markAllAsTouched();
      this.alertService.error(this.t('chat.dialog.nameRequired'));
      return;
    }

    this.submitting = true;
    this.chatSocketService.sendCommand(ChatCommandTypeEnum.CHANNEL_CREATE, {
      type: this.channelForm.value.type,
      name,
      description: (this.channelForm.value.description ?? '').trim() || undefined,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.store.dispatch(ChatActions.loadBootstrap());
          this.dialogRef.close(true);
        },
        error: (ack) => {
          this.submitting = false;
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
